/**
 * Shared utility functions for plant data normalization.
 */

export const titleCase = (s) => {
  if (!s) return '';
  return s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
};

// Strip HTML/script tags and limit to standard characters (letters, digits, spaces, common punctuation)
export const sanitizeText = (s, maxLength = 100) => {
  if (!s) return '';
  const stripped = s.replace(/<[^>]*>/g, '');
  const safe = stripped.replace(/[^\p{L}\p{N}\s.,;:'"()\-!?&/°×#]+/gu, '');
  return safe.slice(0, maxLength);
};

export const NAME_MAX_LENGTH = 100;

// Canonical values per care field — AI output is mapped to the closest match
export const CARE_VOCAB = {
  watering: ['low', 'moderate', 'frequent'],
  light: ['low', 'medium', 'bright indirect', 'full sun'],
  humidity: ['low', 'moderate', 'high'],
  temperature: ['cool', 'moderate', 'warm'],
  toxicity: ['non-toxic', 'toxic to pets', 'toxic'],
  growth_rate: ['slow', 'moderate', 'fast'],
  environment: ['standard household', 'terrarium preferred', 'terrarium or conservatory required'],
  blooms_indoors: ['Yes', 'Rarely', 'No'],
};

export function capitalizeTemperatureUnits(str) {
  if (!str) return str;
  return str.replace(/°f\b/gi, '°F').replace(/°c\b/gi, '°C');
}

function normalizeValue(field, raw) {
  if (!raw) return '';
  const lower = raw.trim().toLowerCase();
  const vocab = CARE_VOCAB[field];
  if (!vocab) return lower;
  // Exact match
  if (vocab.includes(lower)) return lower;
  // Fuzzy: pick first vocab entry that appears as a substring
  for (const v of vocab) {
    if (lower.includes(v)) return v;
  }
  // For temperature, capitalize unit abbreviations (°f → °F, °c → °C)
  if (field === 'temperature') return capitalizeTemperatureUnits(lower);
  return lower;
}

export const normalizePlantData = (data) => ({
  common_name: titleCase(data.common_name),
  scientific_name: titleCase(data.scientific_name),
  care_details: {
    watering: normalizeValue('watering', data.care_details?.watering || data.care_details?.water || ''),
    light: normalizeValue('light', data.care_details?.light || ''),
    humidity: normalizeValue('humidity', data.care_details?.humidity || ''),
    temperature: normalizeValue('temperature', data.care_details?.temperature || ''),
    toxicity: normalizeValue('toxicity', data.care_details?.toxicity || ''),
    toxicity_notes: (data.care_details?.toxicity_notes || '').toString().trim(),
    growth_rate: normalizeValue('growth_rate', data.care_details?.growth_rate || ''),
    environment: normalizeValue('environment', data.care_details?.environment || ''),
    family: titleCase(data.care_details?.family || ''),
    plant_type: titleCase(data.care_details?.plant_type || ''),
  }
});


/**
 * Find a name-only match (same common_name but different photo).
 * Returns the matching plant, or null.
 */
export function findNameOnlyMatch(commonName, imageHash, existingPlants) {
  const normalizedName = titleCase(commonName).toLowerCase();
  return existingPlants.find(p => {
    const existingName = (p.common_name || '').toLowerCase();
    if (existingName !== normalizedName) return false;
    // If exact same photo hash → full duplicate, not a name-only match
    const hashes = p.image_hashes || (p.image_hash ? [p.image_hash] : []);
    if (imageHash && hashes.includes(imageHash)) return false;
    return true;
  }) || null;
}
